import { IS_STG, IS_DEV } from "../data/js/server-constants"
import { trackError } from "./error-tracking"

export const pInt = (num: string | number): number => {
    const n = parseInt(num as string);
    return !isNaN(n) ? n : 0;
};

// An homage to jQuery in 3 lines
export const $ = (sel) => {
    const s = document.querySelectorAll(sel);
    if (s.length == 0) return [];
    return s.length === 1 ? s[0] : Array.from(s);
};
// If you expect an array to map over, be explicit.
export const $$ = (query) => Array.from(document.querySelectorAll(query));

/**
 * Scroll to the first element matching `sel`, if one is still on the page.
 *
 * `$` hands back `[]` for no match and an Array for several, and neither of
 * those has a `scrollIntoView` -- so a bare `$(sel).scrollIntoView()` throws a
 * TypeError the moment the node it assumed is absent. That is not a
 * hypothetical: an async handler awaits a fetch and scrolls in its
 * continuation, by which time the user has navigated and the node is gone
 * (`add-credit-card` on the wallet page, prod 2026-09-11). A scroll is
 * cosmetic, so it must never take the rest of a handler down with it -- least
 * of all one that is in the middle of taking a card.
 *
 * Returns whether anything was scrolled, so a caller can tell the difference.
 */
export const scrollToSelector = (sel: string, options?: ScrollIntoViewOptions): boolean => {
    const found = $(sel);
    const el = Array.isArray(found) ? found[0] : found;
    if (!el || typeof el.scrollIntoView !== "function") return false;
    el.scrollIntoView(options);
    return true;
};
export const changeClasses = (el, className: string): string => (el.className = className);

export const name = (person: { firstName?: string; lastName?: string; username?: string } | null | undefined): string => {
    if (!person || (!person.firstName && !person.lastName && !person.username)) {
        return "Seller";
    }
    const firstName = person?.firstName
        ? person.firstName.slice(0, 1).toUpperCase() + person.firstName.slice(1)
        : "";
    // Only return first name - never show last name or initials for privacy
    return firstName || person.username || "Seller";
};

export const dec2 = (num: string | number): number => {
    const n = parseFloat(num as string);
    if (isNaN(n))  return 0;
    return parseInt((n * 100).toString()) / 100;
};

export const NumberFormat = (number: number | string): string => {
    if (!number) return "";
    const formatter = new Intl.NumberFormat("default", {
        minimumFractionDigits: 0,
    });
    return formatter.format(number as number);
};

export const Currency = (amount: number | string): string => {
    if (!amount) return "$0";
    return `$${NumberFormat(amount)}`;
};

// An ISO 8601 datetime ("2026-09-23T05:36:30.000Z"), which is what a Prisma
// DateTime becomes in JSON. The dash-to-slash rewrite below is for MySQL's
// "2026-09-23 05:36:30" (Safari will not parse it); applied to an ISO string
// it makes "2026/09/23T05:36:30.000Z", which no browser parses, so every date
// and time from an API route printed blank.
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const HAS_ZONE = /(Z|[+-]\d{2}:?\d{2})$/i;

export const ShortDate = (date: Date | string | number | undefined | null): string => {
    if (!date || typeof date == "undefined") return "";
    if (isString(date) && !ISO_DATETIME.test(date as string)) date = (date as string).replace(/-/g, "/");
    date = new Date(date as string | number | Date);
    // Check if date is valid
    if (isNaN(date.getTime())) return "";
    const formatted = new Intl.DateTimeFormat("default", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
    });
    return formatted.format(date);
};

export const makeOneLineAddress = (address: { zipCode?: string; locality?: string; region?: string; country?: string }): string => {
    return (
        (address.zipCode?.length
            ? address.zipCode
            : `${address.locality}, ${address.region}`) +
        (address.country ? `, ${address.country}` : "")
    );
};

export const addressToString = (address: { line1: string; locality: string; region: string; zipCode: string; country?: string }): string => {
    return `${address.line1}, ${address.locality}, ${address.region}, ${
        address.zipCode
    }, ${address.country || ""}`;
};

export const LongDate = (date: Date | string | number | undefined | null): string => {
    if (!date || typeof date == "undefined") return "";
    const dateObj = new Date(date as string | number | Date);
    // Check if date is valid
    if (isNaN(dateObj.getTime())) return "";
    const formatted = new Intl.DateTimeFormat("default", {
        year: "numeric",
        month: "numeric",
        day: "numeric",
    });
    return formatted.format(dateObj);
};

export const ShortTime = (date: Date | string | number | undefined | null): string => {
    if (!date) return "";

    // Handle Date objects, timestamps, and string dates
    let dateObj: Date;
    if (date instanceof Date) {
        dateObj = date;
    } else if (typeof date === 'number') {
        dateObj = new Date(date);
    } else if (typeof date === 'string' && ISO_DATETIME.test(date)) {
        // No zone means UTC here, the same as the MySQL form below.
        dateObj = new Date(HAS_ZONE.test(date) ? date : `${date}Z`);
    } else if (typeof date === 'string') {
        // A MySQL "YYYY-MM-DD HH:MM:SS", stored in UTC
        dateObj = new Date((date + " UTC").replace(/-/g, "/"));
    } else {
        return "";
    }

    // Validate the date is valid
    if (isNaN(dateObj.getTime())) {
        return "";
    }

    const formatted = new Intl.DateTimeFormat("default", {
        hour: "numeric",
        minute: "numeric",
    });
    return formatted.format(dateObj);
};

export const JustDate = (date: Date | string | number | undefined | null): string => {
    if (!date || typeof date === "undefined") return "";

    try {
        const dateStr = typeof date === 'string' ? date.split(" ")[0] : date;
        const d = new Date(
            (dateStr + " UTC").replace(/-/g, "/"),
        ).setHours(0, 0, 0, 0);

        // Validate the date is valid
        if (isNaN(d)) return "";

        return ShortDate(new Date(d));
    } catch (_error) {
        return "";
    }
};

export const h = (text: string | undefined): string => {
    if (typeof text === "undefined") {
        return "";
    }
    return text
        .replace(/&/g, "&amp;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\//g, "&#x2F;")
        .replace(/=/g, "&#x3D;")
        .replace(/`/g, "&#x60;");
};

// Dash and underscores become captialized words
export const humanize = (dashedStr: string): string => {
    if (!isString(dashedStr)) return "";
    dashedStr = dashedStr.replace(/\-and\-/g, " & ");
    let l = dashedStr.toLowerCase().replace(/\-|\_/g, " ");
    l = l[0].toUpperCase() + l.slice(1);
    return l;
};

// Turn any string into a url, with special chatacter conversion
export const dasherize = (str = ""): string => {
    if (str == "") return str;
    let from = "ąàáäâãåæăćčĉęèéëêĝĥìíïîĵłľńňòóöőôõðøśșşšŝťțţŭùúüűûñÿýçżźž",
        to = "aaaaaaaaaccceeeeeghiiiijllnnoooooooossssstttuuuuuunyyczzz";
    from += from.toUpperCase();
    to += to.toUpperCase();
    const toArray = to.split("");
    from += "ß";
    toArray.push("ss");
    let newStr = "" + str.toLowerCase().trim();
    newStr = newStr
        .replace(/\s\&\s/g, " and ")
        .replace(/.{1}/g, function (c) {
            const index = from.indexOf(c);
            return index === -1 ? c : toArray[index];
        })
        .replace(/[^a-zA-Z\d\s-]/g, "")
        .replace(/[-_\s]+/g, "-")
        .replace(/\'|\"|\?|\!|\,/g, "")
        .toLowerCase();
    if (newStr[0] === "-") newStr = newStr.slice(1);
    return newStr.slice(0, 100);
};

// Is it a string?  Who knows...
export const isString = (data: unknown): boolean => typeof data === "string" && (data as string).length > 0;

// For randoming an array
export const shuffleArray = <T>(array: T[]): T[] => {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
};

// There might be a better way to do this.  Array.from()?
export const ObjectToArray = <T>(obj: Record<string, T> | T[] | null | undefined): T[] => {
    if (!obj) return [];
    if (Array.isArray(obj)) return obj;
    return Object.keys(obj).map((key) => (obj as Record<string, T>)[key]);
};

// Deep equals on the cheap.
export const sameObject = (obj1: unknown, obj2: unknown): boolean => {
    return JSON.stringify(obj1) == JSON.stringify(obj2);
};

// Like shuffling cards
export const interleaveArray = <T>(array1: T[], array2: T[]): T[] =>
    array1.map((v, i) => [v, array2[i]]).reduce((a, b) => a.concat(b));

// Small wrapper to get aroud this pushStates nonsense argument signature
export const changePath = (url: string, isBack = false): void => {
    if (isBack) {
        history.replaceState("", "", url);
    } else {
        history.pushState("", "", url);
    }
};

// Formatting Strings
export const padZeros = (num: number | string): string => {
    if (("" + num).length == 1) return "0" + num;
    else return "" + num;
};

// Utility to change the title of the page
export const changeTitle = (title: string): void => {
    $(".meta-head-title").textContent = title;
    $$(".social-meta-title").map((t) => t.setAttribute("content", h(title)));
};

// Utility to change the description of the page
export const changeDescription = (description: string): void => {
    $$(".meta-description").map((t) =>
        t.setAttribute("content", h(description)),
    );
};

// Utility to change the share image of the page
export const changeShareImage = (image: string, alt: string): void => {
    $$(".meta-image").map((t) => t.setAttribute("content", image));
    $$(".meta-image-alt").map((t) => t.setAttribute("content", h(alt)));
};

// Normalizes the pathname
export const removeSlashes = (pathname: string): string => {
    pathname = pathname.trim().split('#')[0];
    if (pathname[0] === "/") pathname = pathname.slice(1);
    if (pathname[pathname.length - 1] === "/")
        pathname = pathname.slice(0, pathname.length - 1);
    return pathname;
};

export const getCanonical = (id: string | number, title: string): string => {
    return getCanonicalURL(`/product/${id}/${dasherize(title)}`);
};

// Helper to create proper url out of a pathname
export const getCanonicalURL = (pathname: string): string => {
    // Determine subdomain based on environment
    let subdomain = "";
    if (IS_DEV) {
        subdomain = "dev.";
    } else if (IS_STG) {
        subdomain = "new.";
    }

    if (typeof window !== "undefined" && window.location.hostname != 'localhost') {
        if (pathname === "") return `https://${window.location.hostname}/`;
        pathname = removeSlashes(pathname);
        return `https://${window.location.hostname}/${pathname}/`;
    } else {
        if (pathname === "") return `https://${subdomain}sellwild.com/`;
        pathname = removeSlashes(pathname);
        return `https://${subdomain}sellwild.com/${pathname}/`;
    }
};

// Utility to change the share canonical URL of the page
export const changeCanonicalURL = (pathname: string): void => {
    $$(".meta-canonical").map((t) =>
        t.setAttribute("href", getCanonicalURL(pathname)),
    );
    $$(".social-canonical").map((t) =>
        t.setAttribute("content", getCanonicalURL(pathname)),
    );
};

// Normalizes selectors and elements that might be used interchangably.  Used below.
const strToEl = (str: string | Element): Element => {
    if (typeof str === "string") return $(str) as Element;
    return str;
};

// For validation
// export const EMAIL_REGEXP = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
// export const EMAIL_REGEXP =
//     /^[\w-']+(\.[\w-']+)*@([a-z0-9-]+(\.[a-z0-9-]+)*?\.[a-z]{2,6}|(\d{1,3}\.){3}\d{1,3})(:\d{4})?$/;

export const EMAIL_REGEXP = /^[\w.+-]+@[\w-]+\.[\w.]+$/;

// Helper to add a class
export const addClass = (selector, cl: string): void => {
    strToEl(selector).classList.add(cl);
};

// Helper to toggle a class
export const toggleClass = (selector, cl: string): void => {
    const el = strToEl(selector);
    el.classList.toggle(cl);
};

// Helper to remove a class
export const rmClass = (selector, cl: string): void => {
    strToEl(selector).classList.remove(cl);
};

// The best event setting function.  Everything is set on the document and bubbles.
// Example: on("click", ".clickableSelector", (event, target) => {
//    event.target wil be wrong, so we have to seperate them.
//    but target is always the correct element for the selector.
// })
export const on = (eventType, selector, eventHandler: (event: Event, target: EventTarget) => void): void => {
    document.addEventListener(
        eventType,
        (event) => {
            let targetElement;
            if (event.target === document || event.target === window)
                targetElement = event.target;
            else targetElement = event.target.closest(selector);
            if (!targetElement) return;
            // console.log("EVENT ON: ", event, targetElement)
            eventHandler(event, targetElement);
        },
        false,
    );
};

// A smalll interface for sending data via event triggers.  Can be receieved with the function below.
// Add an obj argument to inlcude the data in the event.  When received it's in event.detail.
// CustomEvent is global in every browser and in Node 19+, so only document can be missing.
export const emit = (eventName: string, obj = {}): void => {
    const event = new CustomEvent(eventName, {
        detail: obj,
    });
    // console.log("EVENT EMIT", obj);
    if (typeof document != 'undefined') document.dispatchEvent(event);
};

// Counterpart to emit.  Will run the callback whenever an event of that name is emitted.
// Use custom event names that are not reserved by the browser for communication between components
export const receive = (eventName, cb: EventListenerOrEventListenerObject): void => {
    // console.log("EVENT RECEIVE", eventName);
    if (typeof document != 'undefined')  {
        document.removeEventListener(eventName, cb);
        document.addEventListener(eventName, cb);
    }
};

// Helper to add the hidden class.  Does not use display style.  Set .hidden in your css.
export const hide = (selector: string | Element): void => {
    if (typeof selector === "string")
        document.querySelector(selector)?.classList.add("hidden");
    else selector.classList.add("hidden");
};

// Opposite of above.
export const unHide = (selector: string | Element): void => {
    if (typeof selector === "string")
        document.querySelector(selector)?.classList.remove("hidden");
    else selector.classList.remove("hidden");
};

// A way to add scripts to the document as an array of urls.
export const loadScripts = (urls: string | string[]): void => {
    if (typeof urls === "string") appendJSScript(urls);
    if (Array.isArray(urls))
        for (let i = 0; i < urls.length; i++) appendJSScript(urls[i]);
};

// Used by above.
const LOADED_URLS: string[] = [];
const appendJSScript = (url: string): void => {
    if (LOADED_URLS.includes(url)) return; // No duplcates allowed.
    const preloadedScript = document.createElement("script");
    preloadedScript.src = url;
    preloadedScript.async = true;
    document.body.appendChild(preloadedScript);
    LOADED_URLS.push(url);
};

export const createNotification = (obj: Record<string, unknown>): void => {
    emit("notification", obj);
};

export const changeHash = (hash: string): void => {
    window.location.hash = hash;
};

export const googleEvent = (ev: string, obj = {}): void => {
    try {
        if (typeof gtag !== "undefined") gtag("event", ev, obj);
    } catch (e) {
        trackError(e instanceof Error ? e : String(e));
    }
};

export const serialize = function (obj: Record<string, unknown>): string {
    const str: string[] = [];
    for (const p in obj) {
        if (obj.hasOwnProperty(p) && obj[p] !== null && obj[p] !== undefined) {
            // Handle arrays by adding multiple params with same key
            if (Array.isArray(obj[p])) {
                (obj[p] as unknown[]).forEach(val => {
                    str.push(encodeURIComponent(p) + "[]=" + encodeURIComponent(val as string));
                });
            } else if (obj[p] !== '' && obj[p] !== false) {
                str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p] as string));
            }
        }
    }
    return str.join("&");
};

export const MIN_PHONE_NUMBER_LENGTH = 8;
export const MAX_PHONE_NUMBER_LENGTH = 15;
